<template>
  <div class="ppt-page">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>学术管理</el-breadcrumb-item>
      <el-breadcrumb-item>字体管理</el-breadcrumb-item>
      <el-breadcrumb-item>PPT 生成工具</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="ppt-card">
      <div slot="header" class="card-header">
        <span>Excel 一键生成 PPT</span>
      </div>

      <el-form
        ref="pptFormRef"
        :model="pptForm"
        :rules="pptFormRules"
        label-width="120px"
        class="ppt-form"
      >
        <el-form-item label="封面图" prop="coverFile">
          <el-upload
            class="upload-block"
            action="#"
            :auto-upload="false"
            :show-file-list="true"
            :limit="1"
            accept="image/*"
            :file-list="coverFileList"
            :on-change="handleCoverChange"
            :on-remove="handleCoverRemove"
          >
            <el-button size="small" type="primary">选择封面图片</el-button>
            <div slot="tip" class="el-upload__tip">
              建议使用 16:9 比例 JPG/PNG 图片，大小不超过 5M
            </div>
          </el-upload>
        </el-form-item>

        <el-form-item label="背景图" prop="bgFile">
          <el-upload
            class="upload-block"
            action="#"
            :auto-upload="false"
            :show-file-list="true"
            :limit="1"
            accept="image/*"
            :file-list="bgFileList"
            :on-change="handleBgChange"
            :on-remove="handleBgRemove"
          >
            <el-button size="small" type="primary">选择背景图片</el-button>
            <div slot="tip" class="el-upload__tip">
              将作为每一页内容的背景，建议与封面图风格一致
            </div>
          </el-upload>
        </el-form-item>

        <el-form-item label="Excel 文件" prop="excelFile">
          <el-upload
            class="upload-block"
            action="#"
            :auto-upload="false"
            :show-file-list="true"
            :limit="1"
            accept=".xls,.xlsx"
            :file-list="excelFileList"
            :on-change="handleExcelChange"
            :on-remove="handleExcelRemove"
          >
            <el-button size="small" type="primary">选择 Excel 文件</el-button>
            <div slot="tip" class="el-upload__tip">
              默认使用第一个 Sheet，第一行为表头，后续每一行生成一页 PPT 内容
            </div>
          </el-upload>
        </el-form-item>

        <el-form-item label="PPT 文件名">
          <el-input
            v-model="pptForm.fileName"
            placeholder="默认：学术PPT自动生成.pptx"
            style="max-width: 320px"
          />
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            :loading="generating"
            @click="handleGeneratePpt"
          >
            {{ generating ? '生成中...' : '生成 PPT 并下载' }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import PptxGenJS from 'pptxgenjs'

export default {
  name: 'AcademicPptManagement',
  data () {
    return {
      pptForm: {
        coverFile: null,
        bgFile: null,
        excelFile: null,
        fileName: ''
      },
      pptFormRules: {
        coverFile: [
          { required: true, validator: (_, value, cb) => this.fileRequiredValidator('coverFile', cb), trigger: 'change' }
        ],
        bgFile: [
          { required: true, validator: (_, value, cb) => this.fileRequiredValidator('bgFile', cb), trigger: 'change' }
        ],
        excelFile: [
          { required: true, validator: (_, value, cb) => this.fileRequiredValidator('excelFile', cb), trigger: 'change' }
        ]
      },
      coverFileList: [],
      bgFileList: [],
      excelFileList: [],
      generating: false
    }
  },
  methods: {
    fileRequiredValidator (field, callback) {
      if (!this.pptForm[field]) {
        const labelMap = {
          coverFile: '封面图片',
          bgFile: '背景图片',
          excelFile: 'Excel 文件'
        }
        callback(new Error('请上传' + (labelMap[field] || '文件')))
      } else {
        callback()
      }
    },
    handleCoverChange (file, fileList) {
      this.pptForm.coverFile = file.raw || file
      this.coverFileList = fileList
    },
    handleCoverRemove () {
      this.pptForm.coverFile = null
      this.coverFileList = []
    },
    handleBgChange (file, fileList) {
      this.pptForm.bgFile = file.raw || file
      this.bgFileList = fileList
    },
    handleBgRemove () {
      this.pptForm.bgFile = null
      this.bgFileList = []
    },
    handleExcelChange (file, fileList) {
      this.pptForm.excelFile = file.raw || file
      this.excelFileList = fileList
    },
    handleExcelRemove () {
      this.pptForm.excelFile = null
      this.excelFileList = []
    },
    async handleGeneratePpt () {
      this.$refs.pptFormRef.validate(async valid => {
        if (!valid) return
        if (this.generating) return
        this.generating = true
        try {
          const [coverDataUrl, bgDataUrl, rows] = await Promise.all([
            this.readFileAsDataUrl(this.pptForm.coverFile),
            this.readFileAsDataUrl(this.pptForm.bgFile),
            this.readExcelAsRows(this.pptForm.excelFile)
          ])
          if (!rows || !rows.length) {
            this.$message.warning('Excel 文件内容为空')
            this.generating = false
            return
          }
          await this.generatePptFromData({ coverDataUrl, bgDataUrl, rows })
        } catch (e) {
          console.error('generate ppt error', e)
          this.$message.error('生成 PPT 失败，请检查文件格式后重试')
        } finally {
          this.generating = false
        }
      })
    },
    readFileAsDataUrl (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => resolve(e.target.result)
        reader.onerror = err => reject(err)
        reader.readAsDataURL(file)
      })
    },
    readExcelAsRows (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => {
          try {
            const data = new Uint8Array(e.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            const firstSheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[firstSheetName]
            const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
            resolve(rows)
          } catch (err) {
            reject(err)
          }
        }
        reader.onerror = err => reject(err)
        reader.readAsArrayBuffer(file)
      })
    },
    async generatePptFromData ({ coverDataUrl, bgDataUrl, rows }) {
      const pptx = new PptxGenJS()

      // 封面页
      const coverSlide = pptx.addSlide()
      coverSlide.addImage({
        data: coverDataUrl,
        x: 0,
        y: 0,
        w: '100%',
        h: '100%'
      })

      if (rows.length <= 1) {
        const fileName = this.pptForm.fileName || '学术PPT自动生成.pptx'
        await pptx.writeFile({ fileName })
        this.$message.success('PPT 已生成并开始下载')
        return
      }

      const header = rows[0]
      const contentRows = rows.slice(1)

      contentRows.forEach((row, index) => {
        const hasContent = row && row.some(cell => cell !== null && cell !== undefined && cell !== '')
        if (!hasContent) return

        const slide = pptx.addSlide()
        slide.addImage({
          data: bgDataUrl,
          x: 0,
          y: 0,
          w: '100%',
          h: '100%'
        })

        const title = (row[0] && String(row[0])) || `第${index + 1}页`
        slide.addText(title, {
          x: 0.5,
          y: 0.6,
          fontSize: 28,
          bold: true,
          color: '363636'
        })

        const bullets = []
        for (let i = 1; i < row.length; i++) {
          const cell = row[i]
          if (cell === null || cell === undefined || cell === '') continue
          const label = header[i] ? String(header[i]) : `内容${i}`
          bullets.push(`${label}: ${cell}`)
        }

        if (bullets.length) {
          // PptxGenJS v4 需要数组元素为对象，而不是字符串
          const textRuns = bullets.map(line => ({ text: String(line) }))
          slide.addText(textRuns, {
            x: 0.7,
            y: 1.5,
            fontSize: 20,
            color: '363636',
            bullet: true
          })
        }
      })

      const fileName = this.pptForm.fileName || '学术PPT自动生成.pptx'
      await pptx.writeFile({ fileName })
      this.$message.success('PPT 已生成并开始下载')
    }
  }
}
</script>

<style lang="less" scoped>
.ppt-page {
  padding: 16px;
}

.ppt-card {
  margin-top: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 16px;
  font-weight: 600;
}

.ppt-form {
  max-width: 720px;
}

.upload-block {
  display: inline-block;
}
</style>
